const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

class ApiClient {
  constructor() {
    this._token = null;
    this._tokenExpiry = null;
  }

  /**
   * Set JWT token (used by the main app or login flow).
   */
  setToken(token, expiresAt) {
    this._token = token;
    this._tokenExpiry = expiresAt ? new Date(expiresAt) : null;
    if (token) {
      localStorage.setItem('delist_token', token);
      if (expiresAt) localStorage.setItem('delist_token_exp', expiresAt);
    }
  }

  /**
   * Get current token — from memory or localStorage.
   */
  getToken() {
    if (this._token) return this._token;
    const stored = localStorage.getItem('delist_token');
    if (stored) {
      this._token = stored;
      this._tokenExpiry = localStorage.getItem('delist_token_exp');
    }
    return this._token;
  }

  clearToken() {
    this._token = null;
    this._tokenExpiry = null;
    localStorage.removeItem('delist_token');
    localStorage.removeItem('delist_token_exp');
  }

  isAuthenticated() {
    const token = this.getToken();
    if (!token) return false;
    if (this._tokenExpiry && new Date(this._tokenExpiry) < new Date()) {
      this.clearToken();
      return false;
    }
    return true;
  }

  async request(path, options = {}) {
    const url = `${BACKEND_URL}${path}`;
    const headers = { 'Content-Type': 'application/json', ...options.headers };

    // Attach JWT if available
    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const resp = await fetch(url, {
      headers,
      ...options,
    });

    if (resp.status === 401) {
      this.clearToken();
      // Don't throw — let the UI handle auth state
    }

    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`API error ${resp.status}: ${text}`);
    }
    return resp.json();
  }

  // Auth
  async login(email, password) {
    const data = await this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(data.access_token, data.expires_at);
    return data;
  }

  // Dashboard
  getStats() {
    return this.request('/api/stats');
  }

  // Jobs
  listJobs(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/api/delist${qs ? '?' + qs : ''}`);
  }

  getJob(jobId) {
    return this.request(`/api/delist/${jobId}`);
  }

  createJob(data) {
    return this.request('/api/delist', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  cancelJob(jobId) {
    return this.request(`/api/delist/${jobId}`, { method: 'DELETE' });
  }

  restartJob(jobId) {
    return this.request(`/api/delist/${jobId}/restart`, { method: 'POST' });
  }

  getJobEmails(jobId) {
    return this.request(`/api/jobs/${jobId}/emails`);
  }

  // Knowledge Base
  getKnowledgeStats() {
    return this.request('/api/knowledge');
  }

  listBlacklists() {
    return this.request('/api/knowledge/blacklists');
  }

  listLearnings(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/api/knowledge/learnings${qs ? '?' + qs : ''}`);
  }

  // KB Deletion
  deleteLearning(learningId) {
    return this.request(`/api/knowledge/learnings/${learningId}`, { method: 'DELETE' });
  }

  deleteMethod(methodId) {
    return this.request(`/api/knowledge/methods/${methodId}`, { method: 'DELETE' });
  }

  deleteBlacklist(blacklistHost) {
    return this.request(`/api/knowledge/blacklists/${encodeURIComponent(blacklistHost)}`, { method: 'DELETE' });
  }

  // Job Messages (Agent Chat)
  getJobMessages(jobId) {
    return this.request(`/api/delist/${jobId}/messages`);
  }

  sendJobMessage(jobId, content) {
    return this.request(`/api/delist/${jobId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  // User Management
  getUsers() {
    return this.request('/api/users');
  }

  createUser(data) {
    return this.request('/api/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateUser(userId, data) {
    return this.request(`/api/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  deleteUser(userId) {
    return this.request(`/api/users/${userId}`, { method: 'DELETE' });
  }

  // User Settings
  updateSettings(settings) {
    return this.request('/api/auth/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  changePassword(currentPassword, newPassword) {
    return this.request('/api/auth/password', {
      method: 'PUT',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  }

  getMe() {
    return this.request('/api/auth/me');
  }

  // API Usage
  getJobUsage(jobId) {
    return this.request(`/api/jobs/${jobId}/usage`);
  }

  // Billing
  getBalance() {
    return this.request('/api/billing/balance');
  }

  getPackages() {
    return this.request('/api/billing/packages');
  }

  createCheckout(packageId, originUrl) {
    return this.request('/api/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ package_id: packageId, origin_url: originUrl }),
    });
  }

  getCheckoutStatus(sessionId) {
    return this.request(`/api/billing/checkout/status/${sessionId}`);
  }

  setupCard(originUrl, enableAutotopup = false) {
    return this.request('/api/billing/setup-card', {
      method: 'POST',
      body: JSON.stringify({ origin_url: originUrl, enable_autotopup: enableAutotopup }),
    });
  }

  removePaymentMethod() {
    return this.request('/api/billing/payment-method', { method: 'DELETE' });
  }

  updateAutoTopup(settings) {
    return this.request('/api/billing/auto-topup', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  getTransactions(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/api/billing/transactions${qs ? '?' + qs : ''}`);
  }

  // Subusers
  getSubusers() {
    return this.request('/api/subusers');
  }

  createSubuser(data) {
    return this.request('/api/subusers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  deleteSubuser(userId) {
    return this.request(`/api/subusers/${userId}`, { method: 'DELETE' });
  }

  updateSubuser(userId, data) {
    return this.request(`/api/subusers/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Admin Usage
  getAdminUsage() {
    return this.request('/api/admin/usage');
  }

  // Admin Analytics
  getAdminAnalytics(days = 30) {
    return this.request(`/api/admin/analytics?days=${days}`);
  }

  // API Keys
  listApiKeys() {
    return this.request('/api/api-keys');
  }

  createApiKey(name) {
    return this.request('/api/api-keys', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  revokeApiKey(keyId) {
    return this.request(`/api/api-keys/${keyId}`, {
      method: 'DELETE',
    });
  }

  // Admin Unmatched Emails
  getUnmatchedEmails(limit = 50, offset = 0) {
    return this.request(`/api/admin/emails/unmatched?limit=${limit}&offset=${offset}`);
  }

  assignEmailToJob(emailId, jobId) {
    return this.request(`/api/admin/emails/${emailId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ job_id: jobId }),
    });
  }

  dismissEmail(emailId) {
    return this.request(`/api/admin/emails/${emailId}`, {
      method: 'DELETE',
    });
  }

  // Admin Email Blacklist
  getEmailBlacklist() {
    return this.request('/api/admin/email-blacklist');
  }

  removeFromBlacklist(entryId) {
    return this.request(`/api/admin/email-blacklist/${entryId}`, {
      method: 'DELETE',
    });
  }
}

export const api = new ApiClient();
